import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "@/lib/content";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });

    const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
    const overlay = useTransform(scrollYProgress, [0, 1], [0.35, 0.8]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative h-[100svh] w-full overflow-hidden bg-black"
        >
            <motion.div
                style={{ y, scale }}
                className="absolute inset-0 will-change-transform"
            >
                <img
                    src={IMAGES.heroMacro}
                    alt="Leather craftsmanship macro"
                    className="w-full h-full object-cover"
                />
            </motion.div>

            <motion.div
                style={{ opacity: overlay }}
                className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black"
            />

            {/* Top line - hidden on Home since the navbar crest already shows this */}
            {false && (
                <div className="absolute top-28 lg:top-32 left-0 right-0 px-6 sm:px-10 lg:px-16 flex items-center justify-between z-10">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.8 }}
                        className="text-[11px] uppercase tracking-[0.3em] text-white/60 font-mono-custom"
                    >
                        Atelier · Est. 1995
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.9 }}
                        className="hidden sm:block text-[11px] uppercase tracking-[0.3em] text-white/60 font-mono-custom"
                    >
                        Bengaluru · India
                    </motion.p>
                </div>
            )}

            {/* Headline */}
            <div className="absolute inset-0 flex items-end z-10">
                <div className="w-full px-6 sm:px-10 lg:px-16 pb-24 lg:pb-32">
                    <div className="max-w-6xl">
                        <motion.h1
                            initial={{ opacity: 0, y: 60 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 1.4,
                                ease: [0.22, 1, 0.36, 1],
                                delay: 0.2,
                            }}
                            className="font-serif font-medium text-white text-[14vw] sm:text-[11vw] lg:text-[8.2vw] leading-[0.9] tracking-[-0.02em]"
                        >
                            Leather,
                            <br />
                            <span className="italic font-normal text-white/90">
                                lived-in
                            </span>{" "}
                            and
                            <br />
                            made to last.
                        </motion.h1>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, delay: 0.9 }}
                            className="mt-10 lg:mt-14 flex flex-col sm:flex-row sm:items-end justify-between gap-8"
                        >
                            <p className="max-w-md text-sm lg:text-base text-white/70 leading-relaxed font-light">
                                A quiet atelier in Frazer Town. Thirty years of
                                stitched-by-hand footwear, jackets and leather goods —
                                fitted to you, not to the rack.
                            </p>

                            <a
                                href="#appointment"
                                data-testid="hero-cta-book"
                                className="group inline-flex items-center gap-4 border border-white/40 px-8 py-4 text-[11px] uppercase tracking-[0.28em] text-white hover:bg-white hover:text-black transition-all duration-500"
                            >
                                <span>Book an In-Store Visit</span>
                                <span className="w-6 h-px bg-current transition-all duration-500 group-hover:w-10" />
                            </a>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 right-6 sm:right-10 lg:right-16 z-10 flex items-center gap-3 text-white/60"
            >
                <ArrowDown size={14} className="animate-bounce" />
                <span className="text-[10px] uppercase tracking-[0.3em] font-mono-custom">
                    Scroll
                </span>
            </motion.div>
        </section>
    );
}
